/**
 * Verdict derivation for the Live Impact panel (SE-7002, FC-1.1).
 *
 * The two rules the ticket is emphatic about live here, and nowhere else:
 *
 * - **an alert is any rule that is AMBER or RED** — so the chip's count is a
 *   count of RULES, not of layers and not of headlines;
 * - **BREACH the moment one rule is RED**, otherwise OK.
 *
 * The "Sign Player — Breach" Figma frame (node 5:2, chip node 10:7) is known
 * to be wrong on this point: its chip reads "2 alerts" while the frame renders
 * three breached headlines beside it. The build follows the rule; SE-7014
 * carries the frame fix.
 *
 * A rule the club has not configured is neither an alert nor a breach, but it
 * is still rendered — an unconfigured regime that silently disappeared would
 * read as a clean bill of health.
 * @module features/financial-compliance/domain/verdict
 */

import type {
  ComplianceRuleEvaluation,
  LayerHeadline,
  RuleState,
  ScenarioImpactVerdict,
  VerdictState,
} from "../types";

/**
 * Ordering used to pick the single headline that speaks for a layer.
 *
 * Worst-first: a layer with one breach and two on-track rules is a breaching
 * layer, and the breach sentence is the one worth a reader's attention.
 */
const SEVERITY: Record<RuleState, number> = {
  breach: 3,
  "at-risk": 2,
  "on-track": 1,
  "awaiting-data": 0,
  "not-configured": 0,
};

/**
 * Whether a rule counts as an alert: at-risk or breach, never unjudgeable.
 * @param rule - The evaluated rule.
 * @returns True when the rule is an alert.
 */
export const isAlert = (rule: ComplianceRuleEvaluation): boolean =>
  rule.state === "at-risk" || rule.state === "breach";

/**
 * Count the alerts across every evaluated rule.
 * @param rules - Every evaluated rule in the scenario.
 * @returns The number of at-risk or breaching rules.
 */
export const countAlerts = (
  rules: readonly ComplianceRuleEvaluation[]
): number => rules.filter(isAlert).length;

/**
 * Derive the headline verdict state.
 *
 * A rule that is `awaiting-data` is not judgeable, so it is excluded from the
 * OK/BREACH decision alongside `not-configured`: a figure that has not arrived
 * yet must never be reported as compliance. When nothing judgeable is left, the
 * verdict says so rather than claiming `ok` on an empty set.
 *
 * SE-8458/SE-8378 added the third outcome. `awaiting-data` used to be merely
 * ignored, so a club with two green rules and one the server could not compute
 * rendered a confident green — a verdict asserted over figures nobody had.
 * Precedence is now breach > incomplete > ok, because an evaluation on partial
 * figures is a LOWER BOUND: more figures can only add cost, so a bound that
 * already breaches really is a breach, while a bound that merely clears its
 * limits has not established that the club does.
 *
 * `not-configured` and `awaiting-data` are different absences and are kept
 * apart: nothing has been set up, versus something has been set up and could
 * not be computed. Only the latter makes a verdict a lower bound.
 * @param rules - Every evaluated rule in the scenario.
 * @returns `breach`, `incomplete`, `ok`, or `not-configured`.
 */
export const deriveVerdictState = (
  rules: readonly ComplianceRuleEvaluation[]
): VerdictState => {
  const judgeable = rules.filter(
    rule => rule.state !== "not-configured" && rule.state !== "awaiting-data"
  );
  if (judgeable.some(rule => rule.state === "breach")) return "breach";

  const hasUncomputedRule = rules.some(rule => rule.state === "awaiting-data");
  if (hasUncomputedRule) return "incomplete";
  return judgeable.length === 0 ? "not-configured" : "ok";
};

/**
 * "N alert" / "N alerts" — the phrase both compliance surfaces count in.
 *
 * Shared so the pluralisation has one definition; the Plan Status tile
 * (`planStatus.ts`) builds its own labels around this because it reads server
 * rule states rather than a client-computed verdict.
 * @param count - How many rules are amber or red.
 * @returns The phrase.
 */
export const alertsPhrase = (count: number): string =>
  `${count} ${count === 1 ? "alert" : "alerts"}`;

/**
 * The verdict chip's words.
 *
 * The alert count is a count of RULES that are amber or red — which is what
 * makes it possible for three headlines to sit beside a count of three, and
 * why the "2 alerts beside three headlines" in Figma node 5:2 is a design bug
 * rather than a rule this build should reproduce.
 *
 * This is the Live Impact panel's vocabulary. The Plan Status tile deliberately
 * does NOT share all of it: it reads a passing plan as "ON TRACK" where the
 * panel says "OK · compliant", because the tile labels a whole plan and the
 * panel labels one pending action. Only `alertsPhrase` is common to both.
 * @param verdict - The computed verdict.
 * @returns The chip label.
 */
export const verdictChipLabel = (verdict: ScenarioImpactVerdict): string => {
  // A SWITCH, not an if-chain ending in a bare return. This used to end with
  // `return \`BREACH · …\`` as the implicit default, which meant widening
  // `VerdictState` silently labelled the new case a BREACH — no compiler
  // complaint, just a wrong word on the chip. SE-8458 widened it, and that is
  // exactly when the trap fires. Every arm returns, so a new member fails to
  // compile here instead.
  switch (verdict.state) {
    case "not-configured":
      return "NOT CONFIGURED";
    case "ok":
      return "OK · compliant";
    case "incomplete":
      // Says "at least" out loud rather than leaving the reader to infer it.
      // The whole point of the incomplete state (SE-8378) is that the true
      // figure can only be worse than the one shown, and a bare count would
      // read as a complete tally.
      return `INCOMPLETE · at least ${alertsPhrase(verdict.alertCount)}`;
    case "breach":
      return `BREACH · ${alertsPhrase(verdict.alertCount)}`;
  }
};

/**
 * Reduce the rules to exactly one headline per layer, worst rule winning.
 *
 * Layer order follows first appearance in `rules`, so the caller's rule order
 * is the render order and the panel does not reshuffle between keystrokes.
 * @param rules - Every evaluated rule in the scenario.
 * @returns One headline per layer.
 */
export const headlinesByLayer = (
  rules: readonly ComplianceRuleEvaluation[]
): readonly LayerHeadline[] =>
  rules.reduce<readonly LayerHeadline[]>((headlines, rule) => {
    const next: LayerHeadline = {
      layerId: rule.layerId,
      state: rule.state,
      headline: rule.headline,
    };
    const current = headlines.find(
      headline => headline.layerId === rule.layerId
    );
    if (current == null) return [...headlines, next];
    if (SEVERITY[current.state] >= SEVERITY[rule.state]) return headlines;
    return headlines.map(headline =>
      headline.layerId === rule.layerId ? next : headline
    );
  }, []);
